import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Icon from "@/components/Icon";
import api, { apiError } from "@/lib/api";

const INV_STYLE = { pending: "bg-amber-500/10 text-amber-600", accepted: "bg-emerald-500/10 text-emerald-600", expired: "bg-slate-500/10 text-slate-500", revoked: "bg-red-500/10 text-red-600" };
const MAIL_LABEL = { sent: "Email sent", mocked: "Test mode — captured, not delivered", failed: "Email failed", not_configured: "Not sent — email service not configured", never_sent: "Never sent (legacy invite)", queued: "Sending…" };
const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

function InviteRow({ inv, isOwner, onChange }) {
  const [busy, setBusy] = useState("");
  const manage = isOwner || inv.role !== "admin";
  const mailOk = ["sent", "mocked"].includes(inv.email_status);
  const act = async (kind) => {
    if (kind === "revoke" && !window.confirm(`Revoke the invitation for ${inv.email}? The link will stop working.`)) return;
    setBusy(kind);
    try {
      const { data } = await api.post(`/company/invitations/${inv.id}/${kind}`);
      if (kind === "resend") {
        if (data.email.ok) toast.success(data.email.status === "mocked" ? "Resent in test mode (captured, not delivered). Older link no longer works." : "Invitation resent. The previous link no longer works.");
        else toast.error(`Email NOT sent: ${data.email.error}`);
      } else toast.success("Invitation revoked");
      onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  return (
    <div data-testid={`invite-row-${inv.id}`} className="flex flex-wrap items-center gap-3 p-4">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">{inv.name || inv.email} <span className="text-xs font-normal text-muted-foreground">{inv.name ? inv.email : ""}</span></div>
        <div className="text-xs text-muted-foreground">Role <b className="capitalize">{inv.role}</b> · invited by {inv.invited_by_name || "—"} · expires {fmt(inv.expires_at)}</div>
        <div data-testid={`invite-mail-${inv.id}`} className={`text-xs mt-0.5 ${mailOk ? "text-emerald-600" : "text-red-600"}`}><Icon name={mailOk ? "MailCheck" : "MailWarning"} className="h-3 w-3 inline mr-1" />{MAIL_LABEL[inv.email_status] || inv.email_status}{inv.email_error && !mailOk ? `: ${inv.email_error}` : ""}{inv.last_sent_at ? ` · last attempt ${fmt(inv.last_sent_at)}` : ""}</div>
      </div>
      <span data-testid={`invite-status-${inv.id}`} className={`text-xs font-bold rounded-full px-2.5 py-1 capitalize ${INV_STYLE[inv.status]}`}>{inv.status}</span>
      {manage && ["pending", "expired"].includes(inv.status) && <button disabled={!!busy} onClick={() => act("resend")} data-testid={`invite-resend-${inv.id}`} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50">{busy === "resend" ? "Sending…" : "Resend"}</button>}
      {manage && inv.status === "pending" && <button disabled={!!busy} onClick={() => act("revoke")} data-testid={`invite-revoke-${inv.id}`} className="rounded-lg border border-red-500/40 text-red-600 px-3 py-1.5 text-xs font-semibold hover:bg-red-500/10 disabled:opacity-50">Revoke</button>}
    </div>
  );
}

export function InvitationList({ invites, isOwner, onChange }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold mb-2">Invitations ({invites.length})</h2>
      <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="invitations-list">
        {invites.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No invitations yet.</p> : invites.map((i) => <InviteRow key={i.id} inv={i} isOwner={isOwner} onChange={onChange} />)}
      </div>
    </div>
  );
}

export function RoleMatrix({ workspace }) {
  if (!workspace) return null;
  const roles = workspace.roles || [];
  return (
    <div className="mb-8" data-testid="role-permissions">
      <h2 className="text-sm font-bold mb-2">Role permissions</h2>
      <div className="grid md:grid-cols-4 gap-3 mb-3">
        {roles.map((r) => <div key={r.id} className="rounded-xl border border-border bg-card p-3"><div className="text-sm font-bold">{r.label}</div><p className="text-xs text-muted-foreground mt-1">{r.description}</p></div>)}
      </div>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground"><tr><th className="text-left px-4 py-2.5 font-semibold">Capability</th>{roles.map((r) => <th key={r.id} className="px-3 py-2.5 font-semibold">{r.label}</th>)}</tr></thead>
          <tbody>
            {workspace.matrix.map((row) => (
              <tr key={row.cap} className="border-t border-border">
                <td className="px-4 py-2">{row.label}</td>
                {roles.map((r) => <td key={r.id} className="text-center">{row.roles.includes(r.id) ? <Icon name="Check" className="h-4 w-4 inline text-emerald-600" /> : <Icon name="Minus" className="h-4 w-4 inline text-muted-foreground/50" />}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ActivityList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/company/activity").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  return (
    <div data-testid="activity-history">
      <h2 className="text-sm font-bold mb-2">Activity history</h2>
      <div className="rounded-2xl border border-border bg-card divide-y divide-border max-h-96 overflow-y-auto">
        {items.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No activity yet.</p> : items.map((a) => (
          <div key={a.id} className="px-4 py-2.5 text-xs flex flex-wrap gap-x-3">
            <span className="text-muted-foreground w-40">{fmt(a.created_at)}</span>
            <span className="font-semibold">{a.actor_name || a.actor_email}</span>
            <span className="font-mono text-primary">{a.action}</span>
            <span className="truncate">{a.target_name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CreatorLabel({ creator, compact }) {
  if (!creator) return null;
  if (creator.status !== "ok") return <span className="text-[11px] text-muted-foreground italic" data-testid="creator-label">{creator.label}</span>;
  return <span className="text-[11px] text-muted-foreground truncate" data-testid="creator-label" title={creator.email}><Icon name="UserRound" className="h-3 w-3 inline mr-0.5" />{compact ? creator.name : `${creator.name} (${creator.email})`}</span>;
}

export const notifyError = (e) => toast.error(apiError(e.response?.data?.detail));
